import * as Sentry from '@sentry/react';
import { ExtraErrorData } from '@sentry/integrations';
import { Event } from '@sentry/types';
import { log } from 'logging/logger';

/**
 * We want to log to Sentry:
 * - console.error messages
 * - Any other event that's not a console.log/info/warning
 */
function filterOutEvents(event: Event): Event | null {
  // Log everything that's not a console event. This is the default
  if (event.logger !== 'console') {
    return event;
  }

  // Sentry will ignore this event
  return null;
}

export const initSentry = () => {
  return Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    release: 'unknown-blog-release',
    integrations: [
      // Extracts all non-native attributes from the Error object and attaches
      // them to the event as the `extra` data.
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/plugin/#extraerrordata
      new ExtraErrorData(),
    ],
    beforeSend(event: Event) {
      const filteredEvent = filterOutEvents(event);

      /**
       * We're not interested in this event.
       */
      if (!filteredEvent) {
        return null;
      }

      if (process.env.NODE_ENV !== 'production') {
        log('Sentry Would have captured exception:');
        log(filteredEvent);

        return null;
      }

      return filteredEvent;
    },
    /**
     * Avoid reporting errors from any of these domains.
     * https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/#decluttering-sentry
     */
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  });
};
